import { DECKS } from '../../config/decks';
import { dueTodayCount, newCardsStudiedToday, retentionRate, reviewsToday, studyPace } from '../../domain/analytics/analytics';
import { Heatmap } from '../../components/Heatmap';
import type { AppData, Card, DeckId } from '../../types';

interface Props {
  cards: Record<DeckId, Card[]>;
  data: AppData;
  loading: boolean;
  error: string | null;
  onReload: () => void;
  onStartReview: (deckId: DeckId) => void;
}

export function DashboardView({ cards, data, loading, error, onReload, onStartReview }: Props) {
  const totals = DECKS.reduce((acc, deck) => {
    const deckCards = cards[deck.id] ?? [];
    const states = data.decks[deck.id]?.cards ?? {};
    const due = dueTodayCount(deckCards, states);
    const target = studyPace(deckCards, states, data.decks[deck.id]?.examDate ?? null, data.settings);
    const dailyNewLimit = data.settings.maxNewPerDay || target.newPerDay;
    const introducedToday = newCardsStudiedToday(data.history, deck.id);
    const newGoalRemaining = Math.max(0, dailyNewLimit - introducedToday);
    const newGoalTotal = Math.min(dailyNewLimit, dailyNewLimit /* already capped */);
    acc.reviewsDue += due.due;
    acc.learning += due.learning;
    acc.newRemaining += newGoalRemaining;
    acc.newGoal += newGoalTotal;
    acc.newDone += Math.min(introducedToday, dailyNewLimit);
    acc.cards += deckCards.length;
    acc.learned += target.learnedCards;
    return acc;
  }, { reviewsDue: 0, learning: 0, newRemaining: 0, newGoal: 0, newDone: 0, cards: 0, learned: 0 });
  const accuracy = retentionRate(data.history, 7);
  const learnedPct = totals.cards ? Math.round((totals.learned / totals.cards) * 100) : 0;
  const totalDue = totals.reviewsDue + totals.learning;

  return (
    <section className="view active">
      <header className="page-head">
        <div>
          <h1>Hallo 👋</h1>
          <p className="muted">{new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="head-actions"><button className="btn btn-primary" onClick={() => onStartReview(bestDeck(cards, data))}>Heute starten →</button></div>
      </header>

      <div className="stats-grid">
        <Stat icon="🔁" label="Wiederholungen heute" value={totals.reviewsDue} sub={totals.learning ? `+ ${totals.learning} offene Lernschritte` : 'Karten zur Wiederholung'} highlight={totalDue > 0} />
        <Stat icon="✨" label="Neue Karten heute" value={`${totals.newDone} / ${totals.newGoal}`} sub={totals.newRemaining > 0 ? `Noch ${totals.newRemaining} neue Karten lernen` : 'Tagesziel erreicht 🎉'} highlight={totals.newRemaining > 0} />
        <Stat icon="✅" label="Heute gelernt" value={reviewsToday(data.history)} sub="Reviews insgesamt (alle Decks)" />
        <Stat icon="🎯" label="Genauigkeit (7T)" value={accuracy === null ? '–' : `${Math.round(accuracy * 100)}%`} sub="Good/Easy zählt als korrekt" />
        <Stat icon="📈" label="Gesamtfortschritt" value={`${learnedPct}%`} sub={`${totals.learned} von ${totals.cards} Karten begonnen`} />
      </div>

      {error && <div className="toast error">{error}</div>}
      {loading && <div className="panel">Karten werden geladen…</div>}

      <div className="grid-2">
        <div className="panel">
          <div className="panel-head"><h2>Deine Fächer</h2><button className="btn-ghost" onClick={onReload}>⟳</button></div>
          <div className="deck-list">
            {DECKS.map((deck) => {
              const deckCards = cards[deck.id] ?? [];
              const states = data.decks[deck.id]?.cards ?? {};
              const due = dueTodayCount(deckCards, states);
              const target = studyPace(deckCards, states, data.decks[deck.id]?.examDate ?? null, data.settings);
              const retention = retentionRate(data.history, 30, deck.id);
              const dailyNewLimit = data.settings.maxNewPerDay || target.newPerDay;
              const introducedToday = newCardsStudiedToday(data.history, deck.id);
              const newRemainingToday = Math.max(0, dailyNewLimit - introducedToday);
              const hasWork = due.total > 0 || newRemainingToday > 0;
              return (
                <button key={deck.id} className="deck-card" onClick={() => onStartReview(deck.id)}>
                  <div className="deck-icon">{deck.icon}</div>
                  <div className="deck-meta">
                    <div className="deck-name">{deck.name}</div>
                    <div className="deck-sub">{deckCards.length} Karten · {target.learnedCards} begonnen · {target.mature} reif</div>
                    <div className="deck-progress" aria-label={`${Math.round(target.learnedPct * 100)} Prozent begonnen`}><span style={{ width: `${Math.round(target.learnedPct * 100)}%` }} /></div>
                    <div className="overview-metrics">
                      <span className={due.due ? 'metric-due' : ''}>🔁 {due.due} Wdh.</span>
                      {due.learning > 0 && <span className="metric-learning">⏳ {due.learning} Lernschritte</span>}
                      <span className={newRemainingToday ? 'metric-new' : ''}>✨ {newRemainingToday} neu heute</span>
                      <span>🎯 {retention === null ? '–' : `${Math.round(retention * 100)}%`}</span>
                      {target.daysUntilExam && <span>📆 {target.daysUntilExam} T</span>}
                    </div>
                  </div>
                  <span className={`deck-action ${hasWork ? 'has-due' : ''}`}>{hasWork ? 'Lernen' : 'Extra'}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="panel">
          <div className="panel-head"><h2>Lern-Aktivität</h2><span className="muted">letzte 91 Tage</span></div>
          <Heatmap history={data.history} />
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, label, value, sub, highlight }: { icon: string; label: string; value: string | number; sub: string; highlight?: boolean }) {
  return <div className={`stat-card${highlight ? ' stat-card-highlight' : ''}`}><div className="stat-icon">{icon}</div><div className="stat-label">{label}</div><div className="stat-value">{value}</div><div className="stat-sub">{sub}</div></div>;
}

function bestDeck(cards: Record<DeckId, Card[]>, data: AppData): DeckId {
  let best: DeckId = 'bs';
  let score = -1;
  for (const deck of DECKS) {
    const due = dueTodayCount(cards[deck.id] ?? [], data.decks[deck.id]?.cards ?? {}).total;
    if (due > score) {
      best = deck.id;
      score = due;
    }
  }
  return best;
}
